import { IPageDescription, MediaItem, Volume } from '@wix/adi-content-api';
import { CeType } from '@wix/adi-core-types';
import type {
  ColorPalette,
  CompRef,
  DocumentServicesObject,
  SerializedCompStructure,
  SerializedPageStructure,
} from '@wix/document-services-types';
import {
  AiMediaData,
  ContentInjector,
  LegalPageLinkData,
  injectContentFromStructureToStructure,
} from '@wix/editor-content-injector';
import {
  type BusinessTypeInfo,
  IndustryProfileData,
  PageSectionContent,
  UserData,
} from '@wix/editor-content-provider';
import {
  ContentKind,
  PageData,
  getContentCategoryByCeType,
  languageCodeToLanguageName,
} from '@wix/editor-content-provider';
import { ErrorReporter } from '@wix/editor-error-reporter';
import type { KitDefinition, KitInjectionOptions } from '@wix/editor-kits';
import { fetchKitDefinitions, injectKitIntoStructure } from '@wix/editor-kits';
import type {
  AppInstallOption,
  AppInstallOrigin,
} from '@wix/editor-platform-host-integration-apis';
import { getColorsFromImageAndCurrentPalette } from '@wix/editor-theme-utils';
import _ from 'lodash';
import { AiImageGenerator } from './aiImageGenerator/AiImageGenerator';
import {
  AiImageInjectionBusinessTermsBlacklist,
  AiImageInjectionSectionsBlacklist,
} from './aiImageGenerator/consts';
import { UploadEventHandler } from './aiImageGenerator/types';
import {
  AI_GATEWAT_COLORS_FRON_INAGE_AND_PALETTE_PROMPT_ID,
  AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
  ALLOWED_HEADLESS_INSTALL_APP_DEF_IDS,
  ALLOWED_SILENT_INSTALLATION_APPS,
  DEFAULT_LANGUAGE_CODE,
  SiteGeneratorBiEventNumbers,
} from './consts';
import {
  hideMobilePinElements,
  runPageMobileAdjustments,
} from './mobileLayoutAdjustment/mobileLayoutAdjustment';
import type {
  AddedPageWithRef,
  BuildPageOptions,
  CommonBiFn,
  ExternalImageUrlParams,
  FedopsInteraction,
  FooterPresetDefinition,
  FooterPresetStructure,
  FullHomepageStructure,
  GeneratedHomepage,
  GeneratedPage,
  HeaderPresetDefinition,
  HeaderPresetStructure,
  LayoutFamilyDefinition,
  PageStructure,
  PresetStructure,
  SectionPresetDefinition,
  SectionPresetStructure,
  SiteGeneratorOptions,
  UploadExternalUrlFn,
  UploadFileFn,
} from './types';
import { HomepagePresets, LockedPresetIds } from './types';
import { a11yUpgradeTitlesInPage } from './utils/a11yUtils';
import {
  collectPageStructures,
  getSectionAiImageIndex,
  handleImageGenerationEvent,
} from './utils/aiInjectionsUtils';
import { logTextChanges } from './utils/debugUtils';
import {
  dsDebugTrace,
  logger,
  setFedopsInteractions,
  verifyInitContent,
} from './utils/decorators';
import {
  fetchLayoutFamilies,
  fetchPresetStructures,
  getBusinessTermId,
  getFooters,
  getHeaders,
  getSectionsPresets,
} from './utils/fetchDataUtils';
import { getBlankPageStructure } from './utils/getBlankPageStructure';
import {
  adjustLogoLayout,
  filterFooters,
  filterHeaders,
  getFooterStructure,
  getHeaderStructure,
  isFooter,
  isHeader,
} from './utils/headerAndFooterUtils';
import { getFullKitInjectionOptions } from './utils/injectKitUtils';
import { getInjectionIgnoredComponents } from './utils/injectionIgnoredUtils';
import { reorderSitePages } from './utils/pagesUtils';
import { removeEmptyTransparentBoxes } from './utils/removeEmptyTransparentBoxes';
import {
  addPinElementsToAllDesktopPagesAndGetTheirCompRefs,
  addSectionsToMenuIfNeeded,
  adjustAndReplaceHeader,
  canSectionPresetBeAdded,
  createPageStructure,
  filterSectionsByTaasBuisnesType,
  filterHeaderOrFooterByTaasBuisnesType,
  filterSectionsMatrixByDuplicates,
  fixPresetStructureApplicationIds,
  generateSiteLinks,
  getAppDefIdsNotToPersonalize,
  getFilteredAiMedia,
  getHomepagePresetsLocked,
  getKitSiteStructure,
  getMetaSiteId,
  getMetaSiteInstance,
  getSectionsStructures,
  getSiteGeneratorPlatformOrigin,
  getSiteTheme,
  getUniquePageName,
  handleCustomAppPageAsHomepage,
  injectLogoLinksToHeader,
  isAppInstalled,
  isExist,
  isGeneratedHomepageStructure,
  isHomepageStructure,
  navigateToPage,
  removePreviousPage,
  replaceFooter,
  replaceSectionsAnchorNames,
  retrieveAppDefIdsFromStructure,
  setPageTextsSource,
  setSiteTheme,
  setUsesNewAnimations,
  syncAndResetMobileLayout,
  encodeUserData,
} from './utils/siteGeneratorUtils';
import {
  getHomepagePresets,
  isExceptionToStyleRules,
} from './utils/styleRulesUtils';
import { ContentProvider } from './contentProvider/contentProvider';

// TODO eslint block the option to use arrow function as class methods

export class SiteGenerator {
  private userData: UserData;
  private structureId: string;
  private industryId: string;
  private useStagingMode: boolean;
  private usedMediaGlobal: MediaItem[] = [];
  private appDefIds: string[];
  private isColorFromLogoNeeded: boolean = false;
  private isColorFromLogoNewPromptIsNeeded: boolean = false;
  private currentLogoPromptPalette: Partial<ColorPalette>[];
  private headlessInstallApps: (
    appDefIds: string[],
    appsInstallOptions: Record<string, Partial<AppInstallOption>>,
    platformOrigin: AppInstallOrigin,
  ) => Promise<void>;
  private additionalPages: PageData[] = [];
  private readonly isDebugMode: boolean;
  private readonly languageCode: string = DEFAULT_LANGUAGE_CODE;
  private readonly presetsBundleBaseUrl: string;
  private readonly staticMediaUrl: string;
  private readonly shouldUseSgInstallOrigin: boolean;
  private readonly layoutFamiliesPromise: Promise<LayoutFamilyDefinition[]>;
  private readonly kitDefinitionsPromise: Promise<KitDefinition[]>;
  private readonly contentProvider: ContentProvider;
  private readonly contentInjector: ContentInjector;
  private readonly aiImageGenerator: AiImageGenerator;
  private readonly interactionStarted: FedopsInteraction;
  private readonly interactionEnded: FedopsInteraction;
  private readonly translate: (key: string) => string;
  private readonly imageUrlUploadFn?: UploadExternalUrlFn;
  private readonly fileUploadFn?: UploadFileFn;
  private readonly siteCommonBi?: CommonBiFn;
  private readonly isAiTextOnImageOpen?: boolean;
  businessTermId?: string;
  private readonly featureName?: string;

  constructor({
    languageCode,
    presetsBundleBaseUrl,
    staticMediaUrl,
    interactionStarted = _.noop,
    interactionEnded = _.noop,
    isStagingMode = false,
    isDebugMode = false,
    userData,
    translate,
    contentVersion,
    shouldUseSgInstallOrigin,
    isAiTextOnImageOpen,
    imageUrlUploadFn,
    fileUploadFn,
    siteCommonBi,
    metasiteInstance,
    sgChangeCaasToAiContentProvider,
    promptsOverride,
    featureName,
  }: SiteGeneratorOptions) {
    setFedopsInteractions({
      interactionStarted,
      interactionEnded,
    });
    this.interactionStarted = interactionStarted;
    this.interactionEnded = interactionEnded;
    this.shouldUseSgInstallOrigin = shouldUseSgInstallOrigin;
    this.userData = userData;
    this.isDebugMode = isDebugMode;
    this.useStagingMode = isStagingMode;
    this.languageCode = languageCode || DEFAULT_LANGUAGE_CODE;
    this.presetsBundleBaseUrl = presetsBundleBaseUrl;
    this.staticMediaUrl = staticMediaUrl;
    this.contentProvider = new ContentProvider({
      useAiContentProvider: sgChangeCaasToAiContentProvider,
      origin: 'siteGenerator',
      locale: this.languageCode,
      contentVersion,
      metasiteInstance,
      promptsOverride,
      ...userData,
    });
    this.layoutFamiliesPromise = fetchLayoutFamilies(
      isStagingMode,
      presetsBundleBaseUrl,
    );
    this.kitDefinitionsPromise = fetchKitDefinitions(
      isStagingMode,
      presetsBundleBaseUrl,
    );
    this.contentInjector = new ContentInjector();
    this.aiImageGenerator = new AiImageGenerator({
      interactionStarted,
      interactionEnded,
      isAiTextOnImageOpen: isAiTextOnImageOpen,
    });
    this.appDefIds = [];
    this.translate = translate;
    this.imageUrlUploadFn = imageUrlUploadFn;
    this.fileUploadFn = fileUploadFn;
    this.siteCommonBi = siteCommonBi;
    this.isAiTextOnImageOpen = isAiTextOnImageOpen;
    this.featureName = featureName;
  }

  setIsColorFromLogoNeeded(isColorFromLogoNeeded: boolean) {
    this.isColorFromLogoNeeded = isColorFromLogoNeeded;
  }

  getIsColorFromLogoNeeded() {
    return this.isColorFromLogoNeeded;
  }

  setIsColorFromLogoNewPromptIsNeeded(
    isColorFromLogoNewPromptIsNeeded: boolean,
  ) {
    this.isColorFromLogoNewPromptIsNeeded = isColorFromLogoNewPromptIsNeeded;
  }

  getIsColorFromLogoNewPromptIsNeeded() {
    return this.isColorFromLogoNewPromptIsNeeded;
  }

  setBusinessTermId(businessTermId?: string) {
    this.businessTermId = businessTermId;
  }

  getIsAiImageInjectionEnabled() {
    if (!this.businessTermId) return false;
    const isAiImageBusinessTermBlacklisted =
      AiImageInjectionBusinessTermsBlacklist.includes(this.businessTermId);

    return !isAiImageBusinessTermBlacklisted;
  }

  private resetUsedMedia() {
    this.usedMediaGlobal = [];
  }

  @logger('sg_init_content')
  async initContent(
    industryId: string,
    structureId: string,
    businessTypeName: string,
  ): Promise<void> {
    this.industryId = industryId;
    this.structureId = structureId;
    await this.contentProvider.initContent({
      industryId,
      structureId,
      businessTypeName,
      appDefIds: this.appDefIds,
    });
    const profiles = await this.getIndustryProfiles();
    if (profiles.length) {
      this.setIndustryProfile(_.sample(profiles)!.id);
    }
    this.setUserData({ industryId, structureId, businessTypeName });
    const businessTermId = await getBusinessTermId(
      businessTypeName,
      this.languageCode,
    );
    this.setBusinessTermId(businessTermId);
  }

  @verifyInitContent
  async getKits(): Promise<KitDefinition[]> {
    const kitDefinitions = await this.kitDefinitionsPromise;
    let supportedKits = kitDefinitions;
    if (this.languageCode) {
      supportedKits = kitDefinitions.filter(
        (kit) =>
          !kit.unsupportedLanguages ||
          !kit.unsupportedLanguages.includes(this.languageCode),
      );
    }
    try {
      const contentSuggestedKits =
        await this.contentProvider.getSuggestedKits();
      const suggestedKitsIdsToVolumeMap = new Map();
      contentSuggestedKits.forEach(({ id, volume }) => {
        suggestedKitsIdsToVolumeMap.set(id, volume);
      });
      return supportedKits.map((kitDefinition) => ({
        ...kitDefinition,
        volume:
          suggestedKitsIdsToVolumeMap.get(kitDefinition.title) ||
          Volume.Optional,
      }));
    } catch (e) {
      return supportedKits.map((kitDefinition) => ({
        ...kitDefinition,
        volume: Volume.Optional,
      }));
    }
  }

  private getLogoColorPalette() {
    if (
      this.isColorFromLogoNeeded &&
      this.userData.logo?.uri &&
      this.currentLogoPromptPalette
    ) {
      return this.currentLogoPromptPalette[0];
    }
  }

  /**
   * Returns the site theme based on the provided data.
   *
   * By default, the theme is based on the provided `kitDefinition` only.
   *
   * If both `kitDefinitions` and `generatedHomepage` are provided,
   * the `kitDefinition` will be patched with some Welcome Section styles.
   *
   */
  getSiteTheme(
    ds: DocumentServicesObject,
    kitDefinition: KitDefinition,
    kitDefinitions?: KitDefinition[],
    generatedHomepage?: GeneratedHomepage,
  ) {
    const logoColorPalette = this.getLogoColorPalette();
    if (generatedHomepage && kitDefinitions?.length) {
      const kitDefinitionForSiteTheme = this.getDesignKitWithAllowedFonts(
        generatedHomepage,
        kitDefinitions,
        kitDefinition,
      );
      return getSiteTheme(ds, kitDefinitionForSiteTheme, logoColorPalette);
    }
    return getSiteTheme(ds, kitDefinition, logoColorPalette);
  }

  @verifyInitContent
  async getLayoutFamilies(): Promise<LayoutFamilyDefinition[]> {
    const layoutFamilies = await this.layoutFamiliesPromise;
    const layoutFamiliesBlacklist = new Set(
      await this.contentProvider.getLayoutFamilyBlacklist(),
    );
    return layoutFamilies.map((layoutFamily) => ({
      ...layoutFamily,
      volume: layoutFamiliesBlacklist.has(layoutFamily.title)
        ? Volume.Optional
        : Volume.BuiltIn,
    }));
  }

  @verifyInitContent
  async getBlacklistedLayoutFamilies() {
    const layoutFamilies = await this.layoutFamiliesPromise;
    const layoutFamiliesBlacklist = new Set(
      await this.contentProvider.getLayoutFamilyBlacklist(),
    );
    return layoutFamilies.filter(({ title }) =>
      layoutFamiliesBlacklist.has(title),
    );
  }

  setUserData(userData: Partial<UserData>) {
    this.userData = { ...this.userData, ...userData };
    const invalidatedData = encodeUserData(this.userData);
    this.contentProvider.setUserData(invalidatedData);
    this.contentInjector.setUserData(invalidatedData);
  }

  getUserData(): UserData {
    return this.userData;
  }

  setHeadlessInstallAppsFn(
    headlessInstallApps: (
      appDefIds: string[],
      appsInstallOptions: Record<string, Partial<AppInstallOption>>,
      platformOrigin: AppInstallOrigin,
    ) => Promise<void>,
  ) {
    this.headlessInstallApps = headlessInstallApps;
  }

  toggleStagingMode(useStagingMode: boolean) {
    this.useStagingMode = useStagingMode;
  }

  private getPageSections(
    layoutFamilyName: string,
    sectionContents: PageSectionContent[],
  ): Promise<{
    fetchedSections: SectionPresetDefinition[][];
    updatedSectionContents: PageSectionContent[];
  }> {
    return Promise.all(
      sectionContents.map((sectionContent) =>
        getSectionsPresets(
          sectionContent.contentCategory,
          layoutFamilyName,
          this.useStagingMode,
          this.presetsBundleBaseUrl,
        ),
      ),
    )
      .then((sections) => {
        let filteredSections = filterSectionsMatrixByDuplicates(sections);
        if (!this.isAiTextOnImageOpen) {
          filteredSections = filteredSections.map((sections) =>
            sections.filter((section) => !section.title.includes('_TOI ')),
          );
        }
        return filterSectionsByTaasBuisnesType(
          filteredSections,
          this.userData?.businessTypeName,
          this.siteCommonBi,
        );
      })
      .then((filteredSections) => {
        return {
          fetchedSections: filteredSections.filter(
            (sectionsList) => sectionsList.length,
          ),
          updatedSectionContents: sectionContents.filter(
            (_, index) => filteredSections[index].length,
          ),
        };
      });
  }

  @logger('sg_get_headers')
  private getHeaders(
    layoutFamilyName: string,
    lockedPresetIds?: LockedPresetIds,
  ): Promise<HeaderPresetDefinition[]> {
    return getHeaders(
      layoutFamilyName,
      this.useStagingMode,
      this.presetsBundleBaseUrl,
    ).then((headers) => {
      const hasLogo = !!this.userData.logo?.uri;
      const filteredHeaders = filterHeaders(headers, hasLogo);

      if (!filteredHeaders.length) {
        return headers;
      }
      const lockedHeader = filteredHeaders.find(
        ({ _id }) => lockedPresetIds?.header === _id,
      );
      if (lockedHeader) {
        return [lockedHeader];
      }

      return filterHeaderOrFooterByTaasBuisnesType(
        filteredHeaders,
        this.userData?.businessTypeName,
        this.siteCommonBi,
        this.appDefIds,
      );
    });
  }

  @logger('sg_get_footers')
  private getFooters(
    layoutFamilyName: string,
    lockedPresetIds?: LockedPresetIds,
  ): Promise<FooterPresetDefinition[]> {
    return getFooters(
      layoutFamilyName,
      this.useStagingMode,
      this.presetsBundleBaseUrl,
    ).then((footers) => {
      const hasLogo = !!this.userData.logo?.uri;
      const filteredFooters = filterFooters(footers, hasLogo, this.appDefIds);
      if (!filteredFooters.length) {
        return footers;
      }
      const lockedFooter = filteredFooters.find(
        ({ _id }) => lockedPresetIds?.footer === _id,
      );
      if (lockedFooter) {
        return [lockedFooter];
      }

      return filterHeaderOrFooterByTaasBuisnesType(
        filteredFooters,
        this.userData?.businessTypeName,
        this.siteCommonBi,
      );
    });
  }

  private getHomepagePresets(
    fetchedHeaders: HeaderPresetDefinition[],
    fetchedSections: SectionPresetDefinition[][],
    fetchedFooters: FooterPresetDefinition[],
    lockedPresetsIds?: LockedPresetIds,
  ): HomepagePresets {
    if (lockedPresetsIds) {
      const { headerPreset, sectionsPresets, footerPreset } =
        getHomepagePresetsLocked(
          fetchedHeaders,
          fetchedSections,
          fetchedFooters,
          lockedPresetsIds,
        );
      return {
        headerPreset,
        footerPreset,
        sectionsPresets: sectionsPresets.filter(
          Boolean,
        ) as SectionPresetDefinition[],
      };
    }
    return getHomepagePresets(
      fetchedHeaders,
      fetchedSections,
      fetchedFooters,
      isExceptionToStyleRules(this.structureId, this.industryId),
    );
  }

  async buildColorsFromLogoPallete(
    ds: DocumentServicesObject,
    kitDefinition?: KitDefinition,
  ) {
    if (
      !this.isColorFromLogoNeeded ||
      !this.userData.logo?.uri ||
      !kitDefinition
    ) {
      return;
    }
    try {
      this.interactionStarted('sg_get_color_from_logo');
      const { fullPalettes } = await getColorsFromImageAndCurrentPalette({
        imagePath: `media/${this.userData.logo.uri}`,
        currentColorsPalette: kitDefinition.colors,
        currentLinkedColorsPalette: kitDefinition.linkedColors || {},
        metaSiteInstanceId: getMetaSiteInstance(ds),
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptIdToUse: this.isColorFromLogoNewPromptIsNeeded
          ? AI_GATEWAT_COLORS_FRON_INAGE_AND_PALETTE_PROMPT_ID.V1_1
          : AI_GATEWAT_COLORS_FRON_INAGE_AND_PALETTE_PROMPT_ID.V1,
        featureName: this.featureName,
      });
      this.interactionEnded('sg_get_color_from_logo');
      this.currentLogoPromptPalette = fullPalettes;
    } catch (error) {
      this.isColorFromLogoNeeded = false;
      console.log(`Fetch colors from image and color palette failed: ${error}`);
    }
  }

  @logger('sg_build_full_homepage_structure')
  @verifyInitContent
  async buildFullHomepageStructure(
    ds: DocumentServicesObject,
    layoutFamilyName: string,
    homepageData: PageData,
    { injectCaasText = true, injectAiText = true }: BuildPageOptions,
    lockedPresetsIds?: LockedPresetIds,
    legalPages?: LegalPageLinkData[],
  ): Promise<GeneratedHomepage> {
    ErrorReporter.breadcrumb('buildFullHomepageStructure', {
      layoutFamilyName,
      sections: homepageData.sections.map(({ contentCategory }) => {
        contentCategory;
      }),
      lockedPresetsIds,
    });
    const [
      fetchedHeaders,
      { fetchedSections, updatedSectionContents },
      fetchedFooters,
    ] = await Promise.all([
      this.getHeaders(layoutFamilyName, lockedPresetsIds),
      this.getPageSections(layoutFamilyName, homepageData.sections),
      this.getFooters(layoutFamilyName, lockedPresetsIds),
    ]);

    const homepagePresets = this.getHomepagePresets(
      fetchedHeaders,
      fetchedSections,
      fetchedFooters,
      lockedPresetsIds,
    );
    const { headerPreset, sectionsPresets, footerPreset } = homepagePresets;

    ErrorReporter.breadcrumb(
      'buildFullHomepageStructure - chosen section presets',
      {
        sectionsPresets: sectionsPresets.map(
          ({ contentCategory }) => contentCategory,
        ),
        updatedHomepageDataSections: updatedSectionContents.map(
          ({ contentCategory }) => contentCategory,
        ),
      },
    );
    const [header, sections, footer] = await Promise.all([
      getHeaderStructure(headerPreset, this.appDefIds, updatedSectionContents),
      getSectionsStructures(sectionsPresets, updatedSectionContents),
      getFooterStructure(footerPreset, this.userData.socialLinks, legalPages),
    ]);

    replaceSectionsAnchorNames(sections, this.translate);

    const homepageStructure = await this.injectSiteStructureContent(
      ds,
      { injectCaasText, injectAiText },
      {
        header,
        sections,
        footer,
      },
      'homepage',
      legalPages,
    );
    return {
      ...homepageStructure,
      name: homepageData.name,
    };
  }

  getInitialHomepageData(): Promise<IPageDescription> {
    return this.contentProvider.getInitialHomepageData();
  }

  async getHomepageData(ds: DocumentServicesObject): Promise<PageData> {
    const initialHomepageData = await this.getInitialHomepageData();
    if (!ds) {
      throw new Error('no ds provided in getHomepageData');
    }
    const appDefIdsNotToPersonalize = getAppDefIdsNotToPersonalize(ds);
    return this.contentProvider.getHomepageData(
      this.appDefIds,
      appDefIdsNotToPersonalize,
      initialHomepageData,
    );
  }

  private getAllowedFontFromGeneratedHomepage(
    generatedHomePage: GeneratedHomepage,
    allKitDefinitions: KitDefinition[],
  ): { fonts: KitDefinition['fonts'] } | undefined {
    const allowedKitIds =
      generatedHomePage.sections.find((section) => section.kitIds?.length)
        ?.kitIds || [];

    if (!allowedKitIds.length) return;
    const fonts = allKitDefinitions.find(
      (kitDefinition) =>
        kitDefinition._id === _.sample(allowedKitIds) && kitDefinition.fontKit,
    )?.fonts;
    if (fonts) {
      return { fonts };
    }
  }

  private getDesignKitWithAllowedFonts(
    generatedHomepage: GeneratedHomepage,
    allKitDefinitions: KitDefinition[],
    kitDefinition: KitDefinition,
  ): KitDefinition {
    const allowedFont = this.getAllowedFontFromGeneratedHomepage(
      generatedHomepage,
      allKitDefinitions,
    );
    const patchedDesignKit = Object.assign(kitDefinition, allowedFont);
    return patchedDesignKit;
  }

  initAdditionalPagesData() {
    return this.contentProvider.initAdditionalPagesData();
  }

  async getAdditionalPagesData(): Promise<PageData[]> {
    const additionalPagesInitialData = await this.initAdditionalPagesData();
    this.additionalPages = await this.contentProvider.getAdditionalPagesData(
      additionalPagesInitialData,
    );
    return this.additionalPages;
  }

  @logger('sg_build_page_structure')
  @verifyInitContent
  async buildPageStructure(
    ds: DocumentServicesObject,
    layoutFamilyName: string,
    pageData: PageData,
    { injectCaasText = true, injectAiText = true }: BuildPageOptions,
    lockedPresetsIds?: string[],
  ): Promise<GeneratedPage> {
    const { fetchedSections, updatedSectionContents } =
      await this.getPageSections(layoutFamilyName, pageData.sections);
    const sectionsPresets = fetchedSections
      .map((sectionPresetList) => {
        if (lockedPresetsIds) {
          return sectionPresetList.find((section) =>
            lockedPresetsIds.find((presetId) => section._id === presetId),
          );
        }
        return _.sample(sectionPresetList);
      })
      .filter((section) => !!section) as SectionPresetDefinition[];
    const sections = await getSectionsStructures(
      sectionsPresets,
      updatedSectionContents,
    );
    a11yUpgradeTitlesInPage(sections);
    replaceSectionsAnchorNames(sections, this.translate);
    const pageStructure = await this.injectSiteStructureContent(
      ds,
      { injectCaasText, injectAiText },
      { sections },
      pageData.name,
    );
    return {
      ...pageStructure,
      name: pageData.name,
    };
  }

  @logger('sg_inject_site_structure_content')
  @verifyInitContent
  private async injectSiteStructureContent<
    T extends PageStructure | FullHomepageStructure,
  >(
    ds: DocumentServicesObject,
    options: BuildPageOptions,
    pageStructure: T,
    origin: string,
    legalPages?: LegalPageLinkData[],
  ): Promise<T> {
    return this.injectSiteStructureContentWithAiInParallel(
      ds,
      options,
      pageStructure,
      origin,
      legalPages,
    );
  }

  private async injectSiteStructureContentWithAiInParallel<
    T extends PageStructure | FullHomepageStructure,
  >(
    ds: DocumentServicesObject,
    { injectCaasText, injectAiText }: BuildPageOptions,
    pageStructure: T,
    origin: string,
    legalPages?: LegalPageLinkData[],
  ): Promise<T> {
    const injectionIgnoredComponents =
      getInjectionIgnoredComponents(pageStructure);

    const getAiInjectedTextStructure = async () => {
      if (!injectAiText) return;
      const aiTextInjectedSections = _.cloneDeep(pageStructure.sections);

      this.interactionStarted('sg_ai_inject_content');
      const structuresToInject = collectPageStructures(
        aiTextInjectedSections,
        pageStructure,
      );

      const fullPageStructure = getBlankPageStructure(
        'pageUriSEO',
        structuresToInject,
      );

      this.interactionStarted('sg_ds_ai_inject_content');
      const aiInjectedPresets = (await new Promise<SerializedPageStructure>(
        (resolve) => {
          ds.ai.content.getContentByPageStructure(
            this.userData?.businessTypeName ?? '', // TODO handle optional
            this.userData?.businessName ?? '',
            this.userData?.description ?? '',
            fullPageStructure,
            (injectedPageStructure) => {
              this.interactionEnded('sg_ds_ai_inject_content');
              resolve(injectedPageStructure);
            },
            (e) => {
              ErrorReporter.captureException(e, {
                tags: {
                  siteGenerationInjectContent: true,
                },
                extra: {
                  ...this.userData,
                  sectionIds: aiTextInjectedSections.map(({ id }) => id),
                },
              });
              resolve(fullPageStructure);
            },
            languageCodeToLanguageName[this.languageCode],
          );
        },
      )) as SerializedCompStructure;

      if (this.isDebugMode) {
        logTextChanges(
          'AI',
          fullPageStructure.components,
          aiInjectedPresets.components!,
        );
      }

      if (aiInjectedPresets?.components && isHomepageStructure(pageStructure)) {
        const firstPresetAsHeader = aiInjectedPresets.components[0];
        const lastPresetAsFooter = _.last(aiInjectedPresets.components);
        if (
          lastPresetAsFooter &&
          isFooter(lastPresetAsFooter) &&
          pageStructure.footer?.structure
        ) {
          pageStructure.footer.structure = lastPresetAsFooter;
        }
        if (
          firstPresetAsHeader &&
          isHeader(firstPresetAsHeader) &&
          pageStructure.header?.structure
        ) {
          pageStructure.header.structure = firstPresetAsHeader;
        }
      }

      const aiInjectedSections = aiInjectedPresets.components?.filter(
        (preset) => !isHeader(preset) && !isFooter(preset),
      );

      aiTextInjectedSections.forEach((section, index) => {
        if (
          aiInjectedSections?.[index] &&
          section.structure.componentType ===
            aiInjectedSections?.[index].componentType
        ) {
          section.structure = aiInjectedSections[index];
        }
      });
      this.interactionEnded('sg_ai_inject_content');

      return aiTextInjectedSections;
    };

    const getAiInjectedImages = async <
      K extends
        | SectionPresetStructure
        | HeaderPresetStructure
        | FooterPresetStructure,
    >(
      presetStructureContent: K,
    ): Promise<AiMediaData | undefined> => {
      const { structure, id } = presetStructureContent;
      const contentType =
        (presetStructureContent as PageSectionContent).ceType || null;
      const sectionName =
        (presetStructureContent as SectionPresetStructure).name ||
        (contentType as string);
      if (
        !this.getIsAiImageInjectionEnabled() ||
        AiImageInjectionSectionsBlacklist.includes(contentType)
      ) {
        return;
      }

      const createFedopsOptions = (data: { [key: string]: string } = {}) => ({
        customParams: {
          origin: `${origin}/${sectionName}`,
          ...data,
        },
      });
      this.interactionStarted(
        'sg_ai_image_injection_bundle',
        createFedopsOptions(),
      );
      const generatedAiMedia = await this.aiImageGenerator.getImagesByStructure(
        sectionName,
        structure,
        {
          msId: getMetaSiteId(ds),
          msInstance: getMetaSiteInstance(ds),
          businessTerm: this.getUserData().businessTypeName,
          businessTermId: this.businessTermId,
          sectionId: id,
          siteDescription: this.getUserData().description,
          onGenerationEvent: handleImageGenerationEvent(
            this.interactionStarted,
            this.interactionEnded,
            createFedopsOptions,
            origin,
            this.siteCommonBi,
          ),
        },
        {
          imageUrlUploadFn: ({ url, name }: ExternalImageUrlParams) =>
            this.imageUrlUploadFn?.({ url, name }, getMetaSiteInstance(ds)),
          fileUploadFn: this.fileUploadFn,
        },
      );
      this.interactionEnded(
        'sg_ai_image_injection_bundle',
        createFedopsOptions(),
      );

      return getFilteredAiMedia(generatedAiMedia);
    };

    const [aiInjectedStructure, aiImages] = await Promise.all([
      getAiInjectedTextStructure(),
      Promise.all(
        [
          ...(isHomepageStructure(pageStructure) && pageStructure.header
            ? [pageStructure.header]
            : []),
          ...pageStructure.sections,
        ].map(getAiInjectedImages),
      ),
    ]);

    const getStructure = async <
      K extends
        | SectionPresetStructure
        | HeaderPresetStructure
        | FooterPresetStructure,
    >(
      presetStructureContent: K,
      aiImages?: AiMediaData | undefined,
    ): Promise<K> => {
      const { structure } = presetStructureContent;
      const contentType =
        (presetStructureContent as PageSectionContent).ceType || null;
      const contentKind = injectCaasText ? ContentKind.ALL : ContentKind.MEDIA;
      const interactionName = injectCaasText
        ? 'sg_content_injector_inject_preset_structure_full_content'
        : 'sg_content_injector_inject_preset_structure_only_media';

      this.interactionStarted('sg_content_provider_get_content');
      const content = await this.contentProvider.getContent(
        {
          contentType,
          industryId: this.industryId,
          structureId: this.structureId,
        },
        structure,
        contentKind,
      );
      this.interactionEnded('sg_content_provider_get_content');
      this.interactionStarted(interactionName);
      const injectedStructure = this.contentInjector.injectPresetStructure(
        structure,
        content,
        this.usedMediaGlobal,
        this.isDebugMode,
        aiImages,
        injectionIgnoredComponents,
        legalPages,
      );
      this.interactionEnded(interactionName);

      return {
        ...presetStructureContent,
        structure: injectedStructure,
      };
    };

    this.resetUsedMedia();

    const structureSource = aiInjectedStructure || pageStructure.sections;

    const sections = await Promise.all(
      structureSource.map((sectionPreset, index) =>
        getStructure(
          sectionPreset,
          aiImages[getSectionAiImageIndex(index, pageStructure)],
        ),
      ),
    );

    if (injectCaasText && this.isDebugMode) {
      logTextChanges(
        'CAAS',
        pageStructure.sections.map(({ structure }) => structure),
        sections.map(({ structure }) => structure),
      );
    }

    const injectMobileStructure = (structure, mobileStructure) => {
      try {
        injectContentFromStructureToStructure(mobileStructure, structure);
      } catch (e) {
        this.reportError(
          e as Error,
          { injectMobile: true },
          { structure, mobileStructure },
        );
      }
    };

    sections.forEach(({ structure, mobileStructure }) => {
      injectMobileStructure(structure, mobileStructure);
    });

    if (isHomepageStructure(pageStructure)) {
      const headerAiImages = aiImages?.[0];
      const header = pageStructure.header
        ? await getStructure(pageStructure.header, headerAiImages)
        : null;
      if (header) {
        injectMobileStructure(header.structure, header.mobileStructure);
      }
      const footer = pageStructure.footer
        ? await getStructure(pageStructure.footer)
        : null;
      if (footer) {
        injectMobileStructure(footer.structure, footer.mobileStructure);
      }
      adjustLogoLayout(header);
      adjustLogoLayout(footer);
      return {
        header,
        sections,
        footer,
      } as T;
    }

    return {
      sections,
    } as T;
  }

  @logger('sg_verify_widgets_installed')
  private async verifyWidgetsInstalled(
    ds: DocumentServicesObject,
    serializedCompStructures: SerializedCompStructure[],
  ): Promise<void> {
    if (!this.headlessInstallApps) {
      return;
    }
    const uniquePageAppDefIds = [
      ...new Set(
        serializedCompStructures

          .map((structure) => retrieveAppDefIdsFromStructure(structure))
          .flat(),
      ),
    ]
      .filter((id) => ALLOWED_HEADLESS_INSTALL_APP_DEF_IDS.has(id))
      .filter((id) => !isAppInstalled(ds, id));
    if (!uniquePageAppDefIds.length) {
      return;
    }
    const appsInstallOptions = this.getAppsInstallOptions(
      ds,
      uniquePageAppDefIds,
    );
    ErrorReporter.breadcrumb('headlessInstallApps', {
      uniquePageAppDefIds,
      appsInstallOptions,
    });
    try {
      await this.headlessInstallApps(
        uniquePageAppDefIds,
        appsInstallOptions,
        getSiteGeneratorPlatformOrigin(this.shouldUseSgInstallOrigin),
      );
    } catch (e: any) {
      this.reportError(e);
    }
  }

  @logger('sg_add_as_homepage')
  async addAsHomepage(
    ds: DocumentServicesObject,
    generatedHomepage: GeneratedHomepage,
    kitDefinition: KitDefinition,
    legalPages?: LegalPageLinkData[],
    kitDefinitions?: KitDefinition[],
  ): Promise<CompRef | null> {
    const updatedPage = this.getIsAiImageInjectionEnabled()
      ? await this.injectUploadedImagesToPage(generatedHomepage, ds)
      : generatedHomepage;
    const updatedPageWithLegalPagesLinks = legalPages
      ? await this.injectLegalPagesLinksToFooter(updatedPage, legalPages)
      : updatedPage;
    const { name, header, sections, footer } = updatedPageWithLegalPagesLinks;
    const presetsToCheckWidgetsInstalled = [
      ...sections.map(({ structure }) => structure),
      footer?.structure,
    ].filter((s): s is SerializedCompStructure => isExist(s));
    await this.verifyWidgetsInstalled(ds, presetsToCheckWidgetsInstalled);
    setUsesNewAnimations(ds);
    const currentHomepageId = ds.homePage.get();
    const pageRef = await ds.transactions.run(async () => {
      const siteTheme = this.getSiteTheme(
        ds,
        kitDefinition,
        kitDefinitions,
        generatedHomepage,
      );
      setSiteTheme(ds, siteTheme);
      adjustAndReplaceHeader(ds, header);
      replaceFooter(ds, footer?.structure);
      const pageRef = this.addHomePageStructure(
        ds,
        name,
        updatedPageWithLegalPagesLinks,
      );
      if (!pageRef) {
        return null;
      }
      ds.homePage.set(pageRef.id);
      return pageRef;
    });
    if (!pageRef) {
      return null;
    }
    await navigateToPage(ds, pageRef);
    await removePreviousPage(ds, currentHomepageId);
    return pageRef;
  }

  @logger('sg_adjust_mobile')
  private async adjustMobile(
    ds: DocumentServicesObject,
    allPages: AddedPageWithRef[],
    desktopPinElementsCompRef: CompRef[],
  ): Promise<void> {
    await syncAndResetMobileLayout(ds);
    for (const { pageRef, page } of allPages.values()) {
      await runPageMobileAdjustments(ds, pageRef, page, this.isDebugMode);
    }
    await ds.waitForChangesAppliedAsync();
    await hideMobilePinElements(
      ds,
      desktopPinElementsCompRef.map((compRef) =>
        ds.components.getMobileRef(compRef),
      ),
    );
  }

  @logger('sg_run_post_generation')
  @dsDebugTrace
  async runPostGeneration(
    ds: DocumentServicesObject,
    allPages: AddedPageWithRef[],
    layoutFamilyDefinition: LayoutFamilyDefinition,
  ) {
    const pages = allPages.filter((page) => !!page.pageRef?.id);
    reorderSitePages(ds, pages);
    await addSectionsToMenuIfNeeded(ds, pages);
    const desktopPinElementsCompRef =
      await addPinElementsToAllDesktopPagesAndGetTheirCompRefs(
        ds,
        pages,
        layoutFamilyDefinition,
      );

    injectLogoLinksToHeader(ds);
    await generateSiteLinks(ds).catch((err: Error) => {
      this.reportError(err);
    });
    await this.adjustMobile(ds, pages, desktopPinElementsCompRef);
    await removeEmptyTransparentBoxes(ds, pages);
    ds.history.clear();
    if (this.getIsAiImageInjectionEnabled()) {
      this.aiImageGenerator.clearUriData();
    }
  }

  async injectKit<T extends GeneratedHomepage | GeneratedPage>(
    structure: T,
    layoutFamily: LayoutFamilyDefinition,
    kitDefinition: KitDefinition,
    kitInjectionOptions: KitInjectionOptions,
  ): Promise<T> {
    try {
      this.interactionStarted('sg_inject_kit');
      if (!kitDefinition) {
        throw new Error('no kitDefinition provided to injectKit');
      }
      if (!structure.sections) {
        throw new Error('no page sections provided to injectKit');
      }
      const isHomepage = isGeneratedHomepageStructure(structure);
      const { header, footer, sections, name } = isHomepage
        ? structure
        : { ...structure, header: null, footer: null };
      const injectedKitSiteStructure = await injectKitIntoStructure(
        kitDefinition,
        getKitSiteStructure({ header, sections, footer }),
        this.staticMediaUrl,
        getFullKitInjectionOptions(layoutFamily, kitInjectionOptions),
      );
      const injectedStructure = {
        name,
        sections: sections.map((section, index) => ({
          ...section,
          ...injectedKitSiteStructure.sections[index],
        })),
        ...(isHomepage
          ? {
              header: header && {
                ...header,
                ...injectedKitSiteStructure.header,
              },
              footer: footer && {
                ...footer,
                ...injectedKitSiteStructure.footer,
              },
            }
          : {}),
      } as T;
      this.interactionEnded('sg_inject_kit');

      return injectedStructure;
    } catch (e) {
      this.reportError(
        e as Error,
        { siteGenerationApiFlow: true, injectKit: true },
        {
          industryId: this.industryId,
          structureId: this.structureId,
          kitTitle: kitDefinition?.title,
          kitInjectionOptions,
          userData: this.userData,
          layoutFamily: layoutFamily.title,
        },
      );
      return structure;
    }
  }

  @logger('sg_set_chosen_apps')
  setChosenApps(appDefIds: string[]) {
    if (!appDefIds || !Array.isArray(appDefIds)) {
      throw new Error('appDefIds must be an array');
    }
    ErrorReporter.breadcrumb('setChosenApps', { appDefIds });
    this.appDefIds = appDefIds;
  }

  getChosenApps(): string[] {
    return [...this.appDefIds];
  }

  async getSections(
    ceType: CeType,
    layoutFamily: string,
  ): Promise<PresetStructure[]> {
    const contentCategory = getContentCategoryByCeType(ceType);
    const presets = await getSectionsPresets(
      contentCategory,
      layoutFamily,
      this.useStagingMode,
      this.presetsBundleBaseUrl,
    );
    const presetStructures = await Promise.all(
      presets.map(({ presetJsonUrl, presetMobileJsonUrl }) =>
        fetchPresetStructures([presetJsonUrl, presetMobileJsonUrl]),
      ),
    );
    return presetStructures.map(([structure, mobileStructure], i) => ({
      msid: presets[i].msid,
      title: presets[i].title,
      id: presets[i]._id,
      kitIds: presets[i]?.kitIds,
      ceType,
      structure,
      mobileStructure,
      contentCategory,
      sourceTemplateId: '',
    }));
  }

  private addPageStructure(
    ds: DocumentServicesObject,
    pageName: string,
    sections: SectionPresetStructure[],
  ): CompRef | null {
    const filteredSections = sections.filter(canSectionPresetBeAdded);
    if (filteredSections.length === 0) {
      return null;
    }
    const uniquePageUriSeo = getUniquePageName(ds, pageName);
    const pageStructure = createPageStructure(
      uniquePageUriSeo,
      filteredSections,
      false,
    );
    fixPresetStructureApplicationIds(ds, pageStructure);
    ErrorReporter.breadcrumb('addPageStructure', {
      pageName,
      uniquePageUriSeo,
    });
    return ds.pages.add(pageName, pageStructure);
  }

  private addHomePageStructure(
    ds: DocumentServicesObject,
    pageName: string,
    generatedHomepage: GeneratedHomepage,
  ): CompRef | null {
    const { sections } = generatedHomepage;
    if (sections.length === 0) {
      return null;
    }
    const customAppPageRef = handleCustomAppPageAsHomepage(
      ds,
      this.gatherSilentInstallApps(generatedHomepage),
      sections,
    );
    if (customAppPageRef) {
      return customAppPageRef;
    }
    const pageRef = this.addPageStructure(ds, pageName, sections);
    if (pageRef) {
      setPageTextsSource(ds, pageRef);
    }
    return pageRef;
  }

  private async injectUploadedImagesToPage<
    T extends GeneratedHomepage | GeneratedPage,
  >(page: T, ds: DocumentServicesObject): Promise<T> {
    const { sections } = page;

    const uploadEventHandler: UploadEventHandler = ({ type, payload }) => {
      switch (type) {
        case 'onBunchUploadStart':
          this.interactionStarted('sg_ai_image_injection_upload_bundle', {
            customParams: { ...payload },
          });
          break;
        case 'onBunchUploadSuccess':
          this.interactionEnded('sg_ai_image_injection_upload_bundle', {
            customParams: { ...payload },
          });
          break;
        case 'onSingleUploadStart':
          this.interactionStarted('sg_ai_image_injection_upload_single', {
            customParams: { ...payload },
          });
          break;
        case 'onSingleUploadSuccess':
          const { imageUrl: imagesUrl, ...fedopsPayload } = payload;
          this.interactionEnded('sg_ai_image_injection_upload_single', {
            customParams: fedopsPayload,
          });
          this.siteCommonBi?.(
            SiteGeneratorBiEventNumbers.IMAGE_UPLOAD_SUCCESS,
            {
              componentId: payload.compId,
              imagesUrl,
            },
          );
          break;
      }
    };

    const promises = sections.map(async (section) => {
      if (!this.imageUrlUploadFn) return page;
      const { structure: originalStructure, name } = section;
      const aiMediaData = await this.aiImageGenerator.uploadImagesForStructure(
        this.imageUrlUploadFn,
        originalStructure,
        page.name,
        name,
        getMetaSiteInstance(ds),
        uploadEventHandler,
        this.fileUploadFn,
      );
      const newStructure = this.contentInjector.injectPresetStructure(
        originalStructure,
        [],
        [],
        this.isDebugMode,
        aiMediaData,
      );

      return { ...section, structure: newStructure };
    });

    const newSections = await Promise.all(promises);

    return { ...page, sections: newSections };
  }

  private async injectLegalPagesLinksToFooter<T extends GeneratedHomepage>(
    page: T,
    legalPages: LegalPageLinkData[],
  ): Promise<T> {
    const { footer } = page;

    if (footer) {
      const updatedFooterStructure = this.contentInjector.injectPresetStructure(
        footer.structure,
        [],
        [],
        this.isDebugMode,
        undefined,
        undefined,
        legalPages,
        false,
      );

      return {
        ...page,
        footer: { ...footer, structure: updatedFooterStructure },
      };
    }

    return page;
  }

  @logger('sg_add_page')
  async addPage(
    ds: DocumentServicesObject,
    generatedPage: GeneratedPage,
  ): Promise<CompRef | null> {
    const updatedPage = this.getIsAiImageInjectionEnabled()
      ? await this.injectUploadedImagesToPage(generatedPage, ds)
      : generatedPage;
    const { name, sections } = updatedPage;
    const presetsToCheckWidgetsInstalled = [
      ...sections.map(({ structure }) => structure),
    ].filter((s): s is SerializedCompStructure => isExist(s));
    await this.verifyWidgetsInstalled(ds, presetsToCheckWidgetsInstalled);
    const pageRef = await ds.transactions.run(async () => {
      return this.addPageStructure(ds, name, sections);
    });
    if (!pageRef) {
      return null;
    }
    return pageRef;
  }

  @verifyInitContent
  getIndustryProfiles(): Promise<IndustryProfileData[]> {
    return this.contentProvider.getIndustryProfiles();
  }

  @verifyInitContent
  setIndustryProfile(profileId: string): void {
    ErrorReporter.breadcrumb('setIndustryProfile', { profileId });
    this.contentProvider.setProfileId(profileId);
  }

  async getCurrentIndustryProfile(): Promise<IndustryProfileData | null> {
    return this.contentProvider.getCurrentProfile();
  }

  getBusinessTypeInfo(term: string): Promise<BusinessTypeInfo | null> {
    return this.contentProvider.getBusinessTypeByTerm(term);
  }

  @verifyInitContent
  getAppsInstallOptions(
    ds: DocumentServicesObject,
    appDefIds: string[],
  ): Record<string, Partial<AppInstallOption>> {
    const contentAppsOptions =
      this.contentProvider.getAppsInstallOptions(appDefIds);
    const pendingAppsIds = new Set(
      ds.tpa.getPendingApps().map(({ appDefinitionId }) => appDefinitionId),
    );
    appDefIds.forEach((appDefId) => {
      if (pendingAppsIds.has(appDefId) && contentAppsOptions[appDefId]) {
        contentAppsOptions[appDefId].sourceTemplateId = '';
      }
    });
    return contentAppsOptions;
  }

  /**
   * Makes generated images available for reuse
   */
  freeGeneratedImages() {
    this.aiImageGenerator.freeImages();
  }

  /**
   * Removes generated images from cache to enable clean generation
   */
  flushGeneratedImages() {
    this.aiImageGenerator.flushImages();
  }

  gatherSilentInstallApps({
    header,
    sections,
    footer,
  }: GeneratedHomepage): string[] {
    const appDefIds: string[] = [];
    if (header) {
      appDefIds.push(...retrieveAppDefIdsFromStructure(header.structure));
    }
    if (footer) {
      appDefIds.push(...retrieveAppDefIdsFromStructure(footer.structure));
    }
    sections.forEach(({ structure }) => {
      appDefIds.push(...retrieveAppDefIdsFromStructure(structure));
    });
    return [
      ...new Set(
        [...appDefIds, ...this.appDefIds].filter((appDefId) =>
          ALLOWED_SILENT_INSTALLATION_APPS.has(appDefId),
        ),
      ),
    ];
  }

  reportError(
    error: Error,
    tags?: Record<string, any>,
    extra?: Record<string, any>,
  ) {
    ErrorReporter.captureException(error, {
      tags: {
        siteGenerationApiFlow: true,
        ...tags,
      },
      extra,
    });
    if (!this.isDebugMode) {
      return;
    }
    console.error('reportError', error);
  }
}
